import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Spinner,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@pnl/ui';
import { api } from '../lib/api';

const ACTION_TONE: Record<string, 'brand' | 'success' | 'danger' | 'warning' | 'neutral'> = {
  create: 'success',
  update: 'brand',
  delete: 'danger',
};

function actionTone(action: string): 'brand' | 'success' | 'danger' | 'warning' | 'neutral' {
  if (action.endsWith('.delete')) return ACTION_TONE.delete!;
  if (action.endsWith('.create')) return ACTION_TONE.create!;
  if (action.endsWith('.update')) return ACTION_TONE.update!;
  if (action.startsWith('user.')) return 'warning';
  return 'neutral';
}

export function AuditLogPage() {
  const [actionFilter, setActionFilter] = useState('');
  const [entity, setEntity] = useState('');
  const [page, setPage] = useState(1);
  const [openDiff, setOpenDiff] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['audit', { actionFilter, entity, page }],
    queryFn: () =>
      api.listAuditLogs({
        action: actionFilter || undefined,
        entity: entity || undefined,
        page,
        pageSize: 50,
      } as never),
  });

  return (
    <div className="space-y-6">
      <div>
        <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">M1.10</p>
        <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
          Audit log
        </h1>
        <p className="text-ink-500 mt-1 text-sm">
          Toda mutação em tabelas críticas é registrada automaticamente com diff antes/depois.
        </p>
      </div>

      <Card className="px-4 py-3">
        <div className="grid gap-3 md:grid-cols-3">
          <Input
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value);
              setPage(1);
            }}
            placeholder="Filtrar action (ex: User.update)"
            leadingIcon={<Search size={14} />}
          />
          <Input
            value={entity}
            onChange={(e) => {
              setEntity(e.target.value);
              setPage(1);
            }}
            placeholder="Filtrar entity (ex: User, Trader)"
          />
          <div className="text-ink-500 text-right text-xs md:place-self-end md:self-center">
            {query.data ? `${query.data.total} eventos` : '—'}
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        {query.isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size={24} />
          </div>
        ) : query.data && query.data.items.length > 0 ? (
          <>
            <Table>
              <THead>
                <TR>
                  <TH>Quando</TH>
                  <TH>Ação</TH>
                  <TH>Entity</TH>
                  <TH>Ator</TH>
                  <TH>IP</TH>
                  <TH className="text-right">Diff</TH>
                </TR>
              </THead>
              <TBody>
                {query.data.items.map((log) => (
                  <TR key={log.id}>
                    <TD className="text-ink-600 whitespace-nowrap font-mono text-xs">
                      {new Date(log.createdAt).toLocaleString('pt-BR')}
                    </TD>
                    <TD>
                      <Badge tone={actionTone(log.action)}>{log.action}</Badge>
                    </TD>
                    <TD className="font-mono text-xs">
                      {log.entity ?? '—'}
                      {log.entityId && (
                        <span className="text-ink-400 ml-1">({log.entityId.slice(0, 8)}…)</span>
                      )}
                    </TD>
                    <TD>
                      {log.actor ? (
                        <div className="text-xs">
                          <p className="text-ink-900 font-medium">
                            {log.actor.fullName || log.actor.email}
                          </p>
                          {log.actor.fullName && <p className="text-ink-500">{log.actor.email}</p>}
                        </div>
                      ) : (
                        <span className="text-ink-400 text-xs">sistema</span>
                      )}
                    </TD>
                    <TD className="text-ink-500 font-mono text-xs">{log.ip ?? '—'}</TD>
                    <TD className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setOpenDiff(openDiff === log.id ? null : log.id)}
                      >
                        {openDiff === log.id ? 'Esconder' : 'Ver'}
                      </Button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            {openDiff && (
              <div className="border-ink-200 bg-ink-900 border-t p-4">
                <p className="text-2xs text-brand-300 mb-2 font-semibold uppercase tracking-widest">
                  Diff JSON
                </p>
                <pre className="bg-ink-800 text-ink-100 max-h-96 overflow-auto rounded-md p-3 font-mono text-xs">
                  {JSON.stringify(query.data.items.find((l) => l.id === openDiff)?.diff, null, 2)}
                </pre>
              </div>
            )}
            <div className="border-ink-200 bg-ink-50/50 text-ink-500 flex items-center justify-between border-t px-4 py-3 text-xs">
              <span>
                Página {page} de {Math.max(1, Math.ceil(query.data.total / query.data.pageSize))}
              </span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Anterior
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= Math.ceil(query.data.total / query.data.pageSize)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          </>
        ) : (
          <EmptyState
            title="Nenhum evento de auditoria"
            description="As ações futuras serão listadas aqui automaticamente."
          />
        )}
      </Card>
    </div>
  );
}
